import { useEffect, useMemo, useState } from 'react';
import {
  getShops,
  getInvoices,
  getAllDeals,
} from '../../services/adminMockData';

const PERIODS = [
  { key: 'day',   label: 'Vandaag',  days: 1 },
  { key: 'week',  label: 'Week',     days: 7 },
  { key: 'month', label: 'Maand',    days: 30 },
  { key: 'year',  label: 'Jaar',     days: 365 },
];

export default function AdminStatsPage() {
  const [period, setPeriod] = useState('month');
  const [shops, setShops] = useState(() => getShops());
  const [invoices, setInvoices] = useState(() => getInvoices());
  const [deals, setDeals] = useState(() => getAllDeals());

  useEffect(() => {
    const refresh = () => {
      setShops(getShops());
      setInvoices(getInvoices());
      setDeals(getAllDeals());
    };
    window.addEventListener('walkin-deals-change', refresh);
    window.addEventListener('walkin-admin-shops-change', refresh);
    return () => {
      window.removeEventListener('walkin-deals-change', refresh);
      window.removeEventListener('walkin-admin-shops-change', refresh);
    };
  }, []);

  const periodCutoff = useMemo(() => {
    const days = PERIODS.find((p) => p.key === period).days;
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d;
  }, [period]);

  const periodDeals = useMemo(() =>
    deals.filter((d) => !d.createdAt || new Date(d.createdAt) >= periodCutoff),
    [deals, periodCutoff]
  );

  const totals = useMemo(() => {
    const totalViews = periodDeals.reduce((s, d) => s + (d.views || 0), 0);
    const totalRedemptions = periodDeals.reduce((s, d) => s + (d.redemptions || 0), 0);
    const conv = totalViews ? Math.round((totalRedemptions / totalViews) * 100) : 0;
    return { totalViews, totalRedemptions, conv };
  }, [periodDeals]);

  const areaStats = useMemo(() => {
    const map = {};
    periodDeals.forEach((d) => {
      if (!map[d.area]) map[d.area] = { area: d.area, deals: 0, views: 0, redemptions: 0, shops: new Set() };
      map[d.area].deals += 1;
      map[d.area].views += d.views || 0;
      map[d.area].redemptions += d.redemptions || 0;
      if (d.shopName) map[d.area].shops.add(d.shopName);
    });
    return Object.values(map)
      .map((r) => ({ ...r, shops: r.shops.size }))
      .sort((a, b) => b.redemptions - a.redemptions);
  }, [periodDeals]);

  const categoryStats = useMemo(() => {
    const map = {};
    periodDeals.forEach((d) => {
      const c = d.category || 'Overig';
      if (!map[c]) map[c] = { category: c, count: 0, redemptions: 0 };
      map[c].count += 1;
      map[c].redemptions += d.redemptions || 0;
    });
    return Object.values(map).sort((a, b) => b.redemptions - a.redemptions);
  }, [periodDeals]);

  const topDeals = useMemo(() =>
    [...periodDeals]
      .sort((a, b) => (b.redemptions || 0) - (a.redemptions || 0))
      .slice(0, 10),
    [periodDeals]
  );

  const hourly = useMemo(() => {
    // Mock "peak hours" by spreading redemptions across realistic shopping hours
    const arr = new Array(24).fill(0);
    const profile = [0,0,0,0,0,0,0,1,3,5,7,9,12,11,9,8,7,9,7,4,2,1,0,0];
    const totalProfile = profile.reduce((a, b) => a + b, 0);
    periodDeals.forEach((d) => {
      const reds = d.redemptions || 0;
      profile.forEach((w, i) => { arr[i] += Math.round((reds * w) / totalProfile); });
    });
    return arr;
  }, [periodDeals]);

  const maxHour = Math.max(1, ...hourly);

  const revenueStats = useMemo(() => {
    const monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);
    const lastMonthStart = new Date(monthStart);
    lastMonthStart.setMonth(lastMonthStart.getMonth() - 1);
    const lastMonthEnd = new Date(monthStart);

    const mrr = shops.reduce(
      (s, sh) => s + (sh.subscription === 'unlimited' ? 99 : 0),
      0
    );
    const monthSales = invoices
      .filter((i) => new Date(i.date) >= monthStart)
      .reduce((s, i) => s + i.amount, 0);
    const lastMonthSales = invoices
      .filter((i) => new Date(i.date) >= lastMonthStart && new Date(i.date) < lastMonthEnd)
      .reduce((s, i) => s + i.amount, 0);
    const wow = lastMonthSales ? Math.round(((monthSales - lastMonthSales) / lastMonthSales) * 100) : 0;
    const unlimitedCount = shops.filter((s) => s.subscription === 'unlimited').length;
    const tokenCount = shops.length - unlimitedCount;
    return { mrr, monthSales, wow, unlimitedCount, tokenCount };
  }, [shops, invoices]);

  return (
    <div>
      <header className="admin-page-head">
        <div>
          <h1 className="admin-page-title">Statistieken</h1>
          <p className="admin-page-sub">Platform breed inzicht</p>
        </div>
        <div className="admin-tabs">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              className={'admin-tab' + (period === p.key ? ' admin-tab--active' : '')}
              onClick={() => setPeriod(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </header>

      <div className="admin-grid" style={{ marginBottom: 18 }}>
        <div className="admin-stat">
          <span className="admin-stat__label">Deals bekeken</span>
          <span className="admin-stat__value">{totals.totalViews.toLocaleString('nl-NL')}</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Deals ingeleverd</span>
          <span className="admin-stat__value">{totals.totalRedemptions.toLocaleString('nl-NL')}</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Conversie</span>
          <span className="admin-stat__value">{totals.conv}%</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">MRR (Unlimited)</span>
          <span className="admin-stat__value">€{revenueStats.mrr.toLocaleString('nl-NL')}</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Token vs Unlimited</span>
          <span className="admin-stat__value" style={{ fontSize: 18 }}>
            {revenueStats.tokenCount} / {revenueStats.unlimitedCount}
          </span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Omzet groei (MoM)</span>
          <span className={'admin-stat__value' + (revenueStats.wow < 0 ? ' admin-stat__delta--down' : '')}>
            {revenueStats.wow > 0 ? '+' : ''}{revenueStats.wow}%
          </span>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card__title">Meest actieve gebieden</div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Gebied</th><th>Deals</th><th>Views</th><th>Inleveringen</th><th>Shops</th>
              </tr>
            </thead>
            <tbody>
              {areaStats.map((r) => (
                <tr key={r.area}>
                  <td>{r.area}</td>
                  <td>{r.deals}</td>
                  <td>{r.views}</td>
                  <td>{r.redemptions}</td>
                  <td>{r.shops}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card__title">Populairste categorieën</div>
        {categoryStats.map((c) => {
          const max = Math.max(1, ...categoryStats.map((x) => x.redemptions));
          const pct = (c.redemptions / max) * 100;
          return (
            <div className="admin-bar-row" key={c.category}>
              <span>{c.category}</span>
              <div className="admin-bar">
                <div className="admin-bar__fill" style={{ width: `${pct}%` }} />
              </div>
              <span className="admin-bar__value">{c.redemptions}</span>
            </div>
          );
        })}
      </div>

      <div className="admin-card">
        <div className="admin-card__title">Piek uren (inleveringen)</div>
        {hourly.map((v, i) => (
          <div className="admin-bar-row" key={i}>
            <span>{String(i).padStart(2, '0')}:00</span>
            <div className="admin-bar">
              <div className="admin-bar__fill" style={{ width: `${(v / maxHour) * 100}%` }} />
            </div>
            <span className="admin-bar__value">{v}</span>
          </div>
        ))}
      </div>

      <div className="admin-card">
        <div className="admin-card__title">Top 10 deals (inleveringen)</div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>#</th><th>Deal</th><th>Winkel</th><th>Gebied</th><th>Inleveringen</th>
              </tr>
            </thead>
            <tbody>
              {topDeals.map((d, i) => (
                <tr key={d.id}>
                  <td>{i + 1}</td>
                  <td>{d.emoji} {d.title}</td>
                  <td>{d.shopName}</td>
                  <td>{d.area}</td>
                  <td><strong>{d.redemptions || 0}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
